import {
    AvailableSuitesResponse,
    CardType,
    CheckoutFlowType,
    CheckoutProcessId,
    CompletedCheckoutStatusResponse,
    CustomerInfo,
    FailedCheckoutStatusResponse,
    FareOptionsResponse,
    FarePriceErrorCode,
    FarePriceResponse,
    MainGuest,
    OtherGuest,
    PastGuestResponse,
    PaymentId,
    PricesErrorCode,
    PricesResponseV2,
    StartedCheckoutStatusResponse,
    StartPaymentErrorResponse,
    StartPaymentResponse,
    SuiteNumber,
    SuitePayment,
    ValidateCheckoutErrorResponse,
    ValidateCheckoutResponse,
    ValidateQuoteErrorResponse,
    ValidateQuoteResponse,
} from "@ssc-booking/api-contracts"
import * as AirClass from "@ssc-booking/api-contracts/src/air-class"
import { Occupancy } from "@ssc-booking/api-contracts/src/occupancy"
import { actions, createMachine, InterpreterFrom, StateMachine, StateValueFrom } from "xstate"
import { StateNodeConfig } from "xstate/lib/types"
import { UUID } from "@ssc-booking/functional-support/src/uuid"
import { CruisePaymentTermsResponse } from "../../../../_common/dot-com-api-types"
import { RequestResponseChannel } from "../../../../_common/machines/data-load-machine"
import { assertEvent } from "../../../../_common/xstate-helpers"
import { PaymentDeclined, PaymentError, PaymentPartiallySucceeded, PaymentSucceeded } from "../../../payment/common"
import { FareCard, FareCardIndex, FareCardKey, FareCardStatus } from "../../read-models/fare-card"
import { NotificationActor } from "../../../../_common/machines/notification-machine"
import { TrackingData } from "../../tracking"
import { SuitesPricesResponse } from "../receipt-machine"
import { Ev } from "../../../../_common/machines/event"
import { BookingSteps } from "../../../../_common/steps/booking-steps"
import {
    ServerNotificationChannel,
    serverNotificationConfig,
} from "../../../../_common/machines/server-notification-machine/server-notification-machine"
import { flowConfig, flowOptions } from "./e-commerce-machine-flow"
import { readModelsConfig } from "./e-commerce-machine-readmodels"
import { userEventsConfig } from "./e-commerce-machine-user-events"
import {
    ECommerceCompletedCheckoutContext,
    ECommerceContext,
    ECommerceErrorCheckoutContext,
    ECommerceFareSelectedV4Context,
    ECommerceFlightSelectedV4Context,
    ECommerceInitialContext,
    ECommercePayingContext,
    ECommerceReadyToCheckoutContext,
    ECommerceSuiteCategorySelectedV4Context,
    ECommerceSuiteNumberSelectedV4Context,
    FareCardRef,
    SubmitWebQuoteContext,
    VsCodeState,
} from "./e-commerce-machine-context"

type ErrorKind = "generic" | "fatal" | "createQuote"
type NotificationKind =
    | "cannotReload"
    | "selectedSuiteNoMoreAvailable"
    | "cannotSelectSuite"
    | "invalidCaptcha"
    | "noAvailability"
    | "invalidParameter"
    | "checkoutProcessExpired"

type ECommerceTypeState =
    | { value: "flow.uninitialized"; context: ECommerceInitialContext }
    | { value: "flow.restoringCheckout"; context: ECommerceInitialContext }
    | { value: "flow.nothingSelectedV4"; context: ECommerceInitialContext }
    | { value: "flow.nothingSelectedV4.suiteCategoryUnselectable"; context: ECommerceInitialContext }
    | { value: "flow.nothingSelectedV4.suiteCategorySelectable"; context: ECommerceInitialContext }
    | { value: "flow.suiteCategorySelectedV4"; context: ECommerceSuiteCategorySelectedV4Context }
    | { value: "flow.fareSelectedV4"; context: ECommerceFareSelectedV4Context }
    | { value: "flow.fareSelectedV4.suiteNumberUnselectable"; context: ECommerceFareSelectedV4Context }
    | { value: "flow.fareSelectedV4.suiteNumberSelectable"; context: ECommerceFareSelectedV4Context }
    | { value: "flow.suiteNumberSelectedV4"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.webQuote"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.webQuote.validatingWebQuote"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.webQuote.webQuoteValidated"; context: SubmitWebQuoteContext }
    | { value: "flow.courtesyHold"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.courtesyHold.validating"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.courtesyHold.validated"; context: ECommerceReadyToCheckoutContext }
    | { value: "flow.checkout"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.checkout.validating"; context: ECommerceSuiteNumberSelectedV4Context }
    | { value: "flow.checkout.validated"; context: ECommerceReadyToCheckoutContext }
    | { value: "flow.readyToCheckout"; context: ECommerceReadyToCheckoutContext }
    | { value: "flow.readyToCheckout.starting"; context: ECommerceReadyToCheckoutContext }
    | { value: "flow.readyToCheckout.paying"; context: ECommercePayingContext }
    | { value: "flow.readyToCheckout.completed"; context: ECommerceCompletedCheckoutContext }
    | { value: "flow.readyToCheckout.error"; context: ECommerceErrorCheckoutContext }
    | { value: "flow.flightSelected"; context: ECommerceFlightSelectedV4Context }

type ValidateCheckoutPayload = {
    mainGuest: MainGuest
    otherGuests: readonly OtherGuest[]
    trackingData: TrackingData
    captchaSolution: string
    suitePayment: SuitePayment
}
type ValidatingCheckoutPayload = ValidateCheckoutPayload & {
    flowType: CheckoutFlowType
}

type ValidateWebQuotePayload = {
    mainGuest: CustomerInfo
    trackingData: TrackingData
    captchaSolution: string
}
export type ECommerceEvent =
    | Ev<"INITIALIZE">
    | Ev<"NO_CHECKOUT_TO_RESTORE">
    | Ev<"CHECKOUT_RESTORED">
    | Ev<"SET_ACTIVE_STEP", { activeStep: BookingSteps }>
    | Ev<"ACTIVE_STEP_READY", { activeStep: BookingSteps }>
    | Ev<"LOAD_FARE_OPTIONS">
    | Ev<"FARE_OPTIONS_LOADED", { response: FareOptionsResponse }>
    | Ev<"FARE_CARDS_LOADED", { fareCards: readonly FareCard[] }>
    | Ev<"FARE_CARD_CHANGED", { fareCard: FareCard }>
    | Ev<"FARE_CARD_PRICE_CHANGED", { fareCard: FareCard }>
    | Ev<"CHANGE_GUESTS", { guests: Occupancy }>
    | Ev<"GUESTS_CHANGED", { guests: Occupancy }>
    | Ev<"CHANGE_AIR_CLASS", { airClass: AirClass.AirClass; key: FareCardKey }>
    | Ev<"CHANGE_GATEWAY", { gatewayCode: string; key: FareCardKey }>
    | Ev<"ALL_FARES_SELECTABLE", { fareCards: readonly FareCard[] }>
    | Ev<"ALL_FARES_IN_ERROR", { error: Exclude<FareCardStatus, "unknown" | "good"> }>
    | Ev<"SELECT_FARE", { key: FareCardKey; isAutomaticSelection?: boolean }>
    | Ev<"UNSELECT_FARE">
    | Ev<"FARE_SELECTED", { selectedFare: FareCardRef }>
    | Ev<"FARE_UNSELECTED">
    | Ev<"LOAD_FARE_PRICE", { index: FareCardIndex }>
    | Ev<"FARE_PRICE_LOADED", { index: FareCardIndex; response: FarePriceResponse }>
    | Ev<"FARE_PRICE_LOADING", { index: FareCardIndex }>
    | Ev<"FARE_PRICE_ERROR", { index: FareCardIndex; code: FarePriceErrorCode }>
    | Ev<"LOAD_FARE_SUITE_PRICES", { index: FareCardIndex }>
    | Ev<"FARE_SUITE_PRICES_LOADED", { index: FareCardIndex; response: PricesResponseV2 }>
    | Ev<"FARE_SUITE_PRICES_LOADING", { index: FareCardIndex }>
    | Ev<"FARE_SUITE_PRICES_ERROR", { index: FareCardIndex; code: PricesErrorCode }>
    | Ev<"SUITE_PRICES_LOADED", { prices: SuitesPricesResponse }>
    | Ev<"SUITE_PRICES_LOADING">
    | Ev<"CRUISE_PAYMENT_TERMS_LOADED", { response: CruisePaymentTermsResponse[] }>
    | Ev<"SELECT_SUITE_CATEGORY", { suiteCategory: string; isAutomaticSelection?: boolean }>
    | Ev<"UNSELECT_SUITE_CATEGORY">
    | Ev<"SUITE_CATEGORY_SELECTED", { suiteCategory: string }>
    | Ev<"SELECT_QUOTE_SUITE_CATEGORY", { suiteCategory: string }>
    | Ev<"QUOTE_SUITE_CATEGORY_SELECTED", { suiteCategory: string }>
    | Ev<"SUITE_CATEGORY_UNSELECTED">
    | Ev<"AVAILABLE_SUITES_LOADING">
    | Ev<"AVAILABLE_SUITES_LOADED", { response: AvailableSuitesResponse }>
    | Ev<"SET_PREFERRED_SUITE_NUMBER", { suiteNumber: SuiteNumber }>
    | Ev<"SELECT_SUITE_NUMBER", { suiteNumber: SuiteNumber }>
    | Ev<"UNSELECT_SUITE_NUMBER">
    | Ev<"SUITE_NUMBER_SELECTED", { suiteNumber: SuiteNumber }>
    | Ev<"SUITE_NUMBER_UNSELECTED">
    | Ev<"FLIGHT_SELECTED", { flightNumber: string }>
    | Ev<"SELECT_FLIGHT", { flightNumber: string }>
    | Ev<"UNSELECT_FLIGHT">
    | Ev<"FLIGHT_UNSELECTED">
    | Ev<"AVAILABLE_FLIGHTS_LOADED">
    | Ev<"FLIGHT_SEARCH_EXPIRED">
    | Ev<"VALIDATE_VS_CODE", { vsCode: string }>
    | Ev<"VS_CODE_VALID", { response: PastGuestResponse }>
    | Ev<"VS_CODE_NOT_VALID">
    | Ev<"RESET_VS_CODE">
    | Ev<"CHANGE_VS_MEMBER", { maybeVsMember: boolean }>
    | Ev<"VS_CODE_CHANGED", { vsCode: VsCodeState }>
    | Ev<"VALIDATE_WEB_QUOTE", ValidateWebQuotePayload>
    | Ev<"VALIDATING_WEB_QUOTE", ValidateWebQuotePayload>
    | Ev<"WEB_QUOTE_VALIDATED", { response: ValidateQuoteResponse }>
    | Ev<"WEB_QUOTE_NOT_VALIDATED", { response: ValidateQuoteErrorResponse }>
    | Ev<"VALIDATE_CHECKOUT", ValidateCheckoutPayload>
    | Ev<"VALIDATING_CHECKOUT", ValidatingCheckoutPayload>
    | Ev<"VALIDATE_COURTESY_HOLD", ValidateCheckoutPayload>
    | Ev<"CHECKOUT_VALIDATED", { response: ValidateCheckoutResponse }>
    | Ev<"CHECKOUT_NOT_VALIDATED", { response: ValidateCheckoutErrorResponse }>
    | Ev<"LOAD_EXISTING_CHECKOUT", { checkoutId: CheckoutProcessId }>
    | Ev<"SESSION_ID_CHANGED", { sessionId: UUID }>
    | Ev<"CHECKOUT_STARTED", { response: StartedCheckoutStatusResponse }>
    | Ev<"CHECKOUT_COMPLETED", { response: CompletedCheckoutStatusResponse }>
    | Ev<"CHECKOUT_FAILED", { response: FailedCheckoutStatusResponse }>
    | Ev<"START_PAYMENT", { paymentId: PaymentId; trackingId: string; cardType: CardType }>
    | Ev<"PAYMENT_STARTED", { response: StartPaymentResponse }>
    | Ev<"INVALID_CHECKOUT_FOR_PAYMENT", { response: StartPaymentErrorResponse }>
    | Ev<"PAYMENT_CANCELLED">
    | Ev<"PAYMENT_CONFIRMED", { paymentResult: PaymentSucceeded | PaymentPartiallySucceeded }>
    | Ev<"PAYMENT_ERROR", { paymentError: PaymentDeclined | PaymentError }>
    | Ev<"ERROR", { kind: ErrorKind; origin: string; errorCode: string; info: unknown; message: string }>
    | Ev<"NOTIFY", { kind: NotificationKind; origin: string; info: unknown }>
    | Ev<"RESTORE_COMPLETED">

type ECommerceMachine = StateMachine<
    ECommerceContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    ECommerceEvent,
    ECommerceTypeState
>
export type ECommerceService = InterpreterFrom<ECommerceMachine>
export type ECommerceState = ReturnType<ECommerceService["getSnapshot"]>
export type ECommerceSend = ECommerceService["send"]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ECommerceStateNodeConfig = StateNodeConfig<ECommerceContext, any, ECommerceEvent>
export type ECommerceMachineStateValue = StateValueFrom<ECommerceMachine>
export type ECommerceMachineOptions = ECommerceMachine["options"]

export type ECommercePureActions = ReturnType<Parameters<typeof actions.pure<ECommerceContext, ECommerceEvent>>[0]>

export const buildECommerceMachine = (
    requestResponseChannel: RequestResponseChannel,
    serverNotificationChannel: ServerNotificationChannel,
    context: ECommerceContext,
): ECommerceMachine => {
    type Context = ECommerceContext
    type Events = ECommerceEvent
    type TypeStates = ECommerceTypeState

    return createMachine<Context, Events, TypeStates>(
        {
            id: "ecommerce",
            type: "parallel",
            predictableActionArguments: true,
            states: {
                dataLoading: context.flowVersion.stateMachine.dataLoading.state(requestResponseChannel),
                serverNotification: serverNotificationConfig(
                    context.flowVersion.stateMachine.serverNotification.config(serverNotificationChannel),
                ),
                readModels: readModelsConfig(context.suitesSanity, context.flowVersion.stateMachine.receipt),
                flow: flowConfig(context.flowVersion.stateMachine.nothingSelected),
                userEvents: userEventsConfig(context),
            },
        },
        {
            guards: { ...context.flowVersion.stateMachine.dataLoading.options.guards, ...flowOptions.guards },
            actions: {
                ...context.flowVersion.stateMachine.dataLoading.options.actions,
                ...flowOptions.actions,
                sendError: actions.sendTo<Context, Events, NotificationActor>(
                    (ctx) => ctx.services.notification,
                    (_, ev) => {
                        assertEvent(ev, "ERROR")
                        return ev
                    },
                ),
                sendNotify: actions.sendTo<Context, Events, NotificationActor>(
                    (ctx) => ctx.services.notification,
                    (_, ev) => {
                        assertEvent(ev, "NOTIFY")
                        return ev
                    },
                ),

                logMe: actions.log<ECommerceContext, ECommerceEvent>((ctx, ev) => ({ type: ev.type, ctx, ev })),
            },
        },
    )
}
